/**
 * SFrame162 — Legal & Important Documents (secure-inventory si-6)
 * Tiles → upload/metadata modal → per-section document list.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Download, Pencil, RefreshCw, Trash2 } from 'lucide-react';
import { SettlementStepPage } from '../../settlement';
import Modal from '../../ui/Modal';
import { SETTLEMENT_PHASE_CONFIGS } from '../../../utils/settlementFlowConfig';
import { useSettlementSession } from '../../../hooks/useSettlementSession';
import { useSettlement } from '../../../contexts/SettlementContext';
import {
  deleteSettlementDocument,
  downloadSettlementDocument,
  uploadSettlementDocument,
} from '../../../utils/settlementDocuments';

const step = SETTLEMENT_PHASE_CONFIGS['secure-inventory'].steps.find((s) => s.id === 'si-6')!;
const PHASE = 'secure-inventory' as const;
const FRAME_ID = 'sframe-162';

/* ---------- types ---------- */

type DocStatus = 'Missing' | 'Uploaded' | 'Verified';
type DocAccessStatus = 'Accessible' | 'Not found' | 'Unknown';

interface LegalImportantDoc {
  id: string;
  section: 'legal' | 'important';
  type: string;
  name: string;
  description: string;
  notes: string;
  status: DocStatus;
  accessStatus: DocAccessStatus;
  fileId?: string;
  fileName?: string;
}

interface SFrame162Persisted {
  legalDocs: LegalImportantDoc[];
  importantDocs: LegalImportantDoc[];
}

/* ---------- tile definitions ---------- */

interface TileDef {
  type: string;
  label: string;
}

const LEGAL_TILES: TileDef[] = [
  { type: 'will', label: 'Last Will and Testament' },
  { type: 'trust', label: 'Living Trust' },
  { type: 'power_of_attorney', label: 'Power of Attorney' },
  { type: 'healthcare_directive', label: 'Healthcare Directive' },
  { type: 'other_legal', label: 'Other Legal Documents' },
];

const IMPORTANT_TILES: TileDef[] = [
  { type: 'birth_certificate', label: 'Birth Certificate' },
  { type: 'social_security', label: 'Social Security Card' },
  { type: 'marriage_certificate', label: 'Marriage Certificate' },
  { type: 'divorce_decree', label: 'Divorce Decree' },
  { type: 'military_records', label: 'Military Records' },
  { type: 'other_important', label: 'Other Important Documents' },
];

const ALL_TYPES = [...LEGAL_TILES, ...IMPORTANT_TILES];

const DOC_STATUSES: DocStatus[] = ['Missing', 'Uploaded', 'Verified'];
const ACCESS_STATUSES: DocAccessStatus[] = ['Accessible', 'Not found', 'Unknown'];

/* ---------- helpers ---------- */

const nid = () => `n-${Math.random().toString(36).slice(2, 9)}`;

function typeLabel(type: string): string {
  return ALL_TYPES.find((t) => t.type === type)?.label ?? type;
}

function statusBadge(status: DocStatus): string {
  if (status === 'Verified') return 'bg-emerald-50 text-emerald-800 border-emerald-200';
  if (status === 'Uploaded') return 'bg-amber-50 text-amber-800 border-amber-200';
  return 'bg-stone-100 text-stone-700 border-stone-200';
}

function emptyDoc(section: 'legal' | 'important', type: string): LegalImportantDoc {
  return {
    id: nid(),
    section,
    type,
    name: '',
    description: '',
    notes: '',
    status: 'Missing',
    accessStatus: 'Unknown',
  };
}

function loadPersisted(raw: unknown): SFrame162Persisted {
  const base = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    legalDocs: Array.isArray(base.legalDocs) ? (base.legalDocs as LegalImportantDoc[]) : [],
    importantDocs: Array.isArray(base.importantDocs)
      ? (base.importantDocs as LegalImportantDoc[])
      : [],
  };
}

/* ---------- component ---------- */

export default function SFrame162Legal() {
  const { estateId } = useParams<{ estateId: string }>();
  const { isArchived } = useSettlement();
  const { loadFrameData, saveFrameData } = useSettlementSession(estateId ?? null, {
    readOnly: isArchived,
  });

  const [legalDocs, setLegalDocs] = useState<LegalImportantDoc[]>([]);
  const [importantDocs, setImportantDocs] = useState<LegalImportantDoc[]>([]);
  const [hydrated, setHydrated] = useState(!estateId);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState<LegalImportantDoc | null>(null);
  const [isReplace, setIsReplace] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const mergedRef = useRef<SFrame162Persisted>({ legalDocs: [], importantDocs: [] });

  /* ---- load ---- */
  useEffect(() => {
    if (!estateId) {
      setHydrated(true);
      return;
    }
    let cancelled = false;
    void (async () => {
      const fd = await loadFrameData(FRAME_ID);
      if (cancelled) return;
      const parsed = loadPersisted(fd?.data);
      mergedRef.current = parsed;
      setLegalDocs(parsed.legalDocs);
      setImportantDocs(parsed.importantDocs);
      setHydrated(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [estateId, loadFrameData]);

  /* ---- auto-save ---- */
  useEffect(() => {
    if (!estateId || !hydrated || isArchived) return;
    const t = window.setTimeout(() => {
      const next: SFrame162Persisted = { legalDocs, importantDocs };
      mergedRef.current = next;
      void saveFrameData(FRAME_ID, next);
    }, 600);
    return () => window.clearTimeout(t);
  }, [legalDocs, importantDocs, estateId, hydrated, isArchived, saveFrameData]);

  /* ---- modal helpers ---- */

  const openAdd = (tile: TileDef, section: 'legal' | 'important') => {
    setEditingDoc(emptyDoc(section, tile.type));
    setIsReplace(false);
    setPendingFile(null);
    setModalOpen(true);
  };

  const openEdit = (doc: LegalImportantDoc) => {
    setEditingDoc({ ...doc });
    setIsReplace(false);
    setPendingFile(null);
    setModalOpen(true);
  };

  const openReplace = (doc: LegalImportantDoc) => {
    setEditingDoc({ ...doc });
    setIsReplace(true);
    setPendingFile(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingDoc(null);
    setPendingFile(null);
    setIsReplace(false);
  };

  const patch = useCallback((p: Partial<LegalImportantDoc>) => {
    setEditingDoc((d) => (d ? { ...d, ...p } : d));
  }, []);

  const saveModal = async () => {
    if (!editingDoc) return;
    setUploading(true);
    try {
      let fileId = editingDoc.fileId;
      let fileName = editingDoc.fileName;

      if (pendingFile && estateId) {
        // Replace mode: delete old file first
        if (isReplace && fileId) {
          await deleteSettlementDocument(estateId, fileId);
        }
        const uploaded = await uploadSettlementDocument({
          estateId,
          phase: PHASE,
          stepId: step.id,
          file: pendingFile,
          description: editingDoc.name || pendingFile.name,
        });
        fileId = uploaded.id;
        fileName = uploaded.filename;
      } else if (isReplace && fileId && estateId) {
        // Replace triggered but no new file chosen — clear attachment
        await deleteSettlementDocument(estateId, fileId);
        fileId = undefined;
        fileName = undefined;
      }

      const saved: LegalImportantDoc = { ...editingDoc, fileId, fileName };

      if (saved.section === 'legal') {
        setLegalDocs((prev) => {
          const exists = prev.some((d) => d.id === saved.id);
          return exists ? prev.map((d) => (d.id === saved.id ? saved : d)) : [...prev, saved];
        });
      } else {
        setImportantDocs((prev) => {
          const exists = prev.some((d) => d.id === saved.id);
          return exists ? prev.map((d) => (d.id === saved.id ? saved : d)) : [...prev, saved];
        });
      }
      closeModal();
    } finally {
      setUploading(false);
    }
  };

  const deleteDoc = async (doc: LegalImportantDoc) => {
    if (doc.fileId && estateId) {
      await deleteSettlementDocument(estateId, doc.fileId);
    }
    if (doc.section === 'legal') {
      setLegalDocs((prev) => prev.filter((d) => d.id !== doc.id));
    } else {
      setImportantDocs((prev) => prev.filter((d) => d.id !== doc.id));
    }
  };

  const handleDownload = (doc: LegalImportantDoc) => {
    if (!doc.fileId || !estateId) return;
    void downloadSettlementDocument(estateId, doc.fileId, doc.fileName ?? doc.name);
  };

  /* ---- derived ---- */
  const isEdit = editingDoc ? [...legalDocs, ...importantDocs].some((d) => d.id === editingDoc.id) : false;

  /* ---- render helpers ---- */

  function TileGrid({
    tiles,
    section,
    docs,
  }: {
    tiles: TileDef[];
    section: 'legal' | 'important';
    docs: LegalImportantDoc[];
  }) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        {tiles.map((tile) => {
          const count = docs.filter((d) => d.type === tile.type).length;
          return (
            <button
              key={tile.type}
              type="button"
              disabled={isArchived}
              onClick={() => openAdd(tile, section)}
              className="flex flex-col items-start rounded-xl border border-stone-200 bg-white px-4 py-3 text-left shadow-sm transition-colors hover:border-cyan-400 hover:bg-cyan-50 disabled:opacity-50"
            >
              <span className="text-sm font-semibold text-stone-800">{tile.label}</span>
              <span className="mt-1 text-xs text-stone-500">
                {count === 0 ? 'None added' : `${count} added`}
              </span>
              <span className="mt-1 text-xs font-medium text-cyan-700">+ Add</span>
            </button>
          );
        })}
      </div>
    );
  }

  function DocList({ docs }: { docs: LegalImportantDoc[] }) {
    if (docs.length === 0) {
      return (
        <p className="text-sm text-stone-400 italic py-4">
          No documents uploaded yet. Use the tiles above to add a document.
        </p>
      );
    }
    return (
      <div className="space-y-3">
        {docs.map((doc) => (
          <div key={doc.id} className="rounded-xl border border-stone-200 bg-stone-50/50 p-4">
            <div className="flex justify-between gap-2 mb-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-stone-900 truncate">
                  {doc.name || 'Untitled document'}
                </p>
                <p className="text-xs text-stone-500 mt-0.5">{typeLabel(doc.type)}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                {doc.fileId ? (
                  <button
                    type="button"
                    aria-label="Download"
                    onClick={() => handleDownload(doc)}
                    className="rounded p-1 text-stone-500 hover:bg-stone-100 hover:text-cyan-700"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                ) : null}
                <button
                  type="button"
                  disabled={isArchived}
                  aria-label="Edit"
                  onClick={() => openEdit(doc)}
                  className="rounded p-1 text-stone-500 hover:bg-stone-100 hover:text-stone-800 disabled:opacity-50"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  disabled={isArchived}
                  aria-label="Replace file"
                  onClick={() => openReplace(doc)}
                  className="rounded p-1 text-stone-500 hover:bg-stone-100 hover:text-stone-800 disabled:opacity-50"
                  title="Replace"
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  disabled={isArchived}
                  aria-label="Delete"
                  onClick={() => void deleteDoc(doc)}
                  className="rounded p-1 text-stone-500 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 text-xs mt-2">
              <div>
                <dt className="text-stone-500">Status</dt>
                <dd>
                  <span
                    className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium ${statusBadge(doc.status)}`}
                  >
                    {doc.status}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-stone-500">Access</dt>
                <dd className="text-stone-700">{doc.accessStatus}</dd>
              </div>
              {doc.fileName ? (
                <div>
                  <dt className="text-stone-500">File</dt>
                  <dd className="text-stone-700 truncate">{doc.fileName}</dd>
                </div>
              ) : null}
              {doc.description ? (
                <div className="col-span-2 sm:col-span-3">
                  <dt className="text-stone-500">Description</dt>
                  <dd className="text-stone-700">{doc.description}</dd>
                </div>
              ) : null}
            </dl>
          </div>
        ))}
      </div>
    );
  }

  /* ---- main render ---- */

  return (
    <SettlementStepPage
      step={step}
      phase="secure-inventory"
      overviewRoute="/sframe-143"
      hidePlannerBanner
      hideNotes
      hideDocuments
      statusControl="segmented"
    >
      {/* Section 1: Legal Documents */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-blue-900 mb-1">Legal Documents</h2>
        <p className="text-sm text-stone-600 mb-4">
          Authority and instruction documents required to act on the estate.
        </p>
        <TileGrid tiles={LEGAL_TILES} section="legal" docs={legalDocs} />
        <DocList docs={legalDocs} />
      </section>

      {/* Section 2: Important Documents */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-blue-900 mb-1">Important Documents</h2>
        <p className="text-sm text-stone-600 mb-4">
          Identity and supporting documents required for verification and processing.
        </p>
        <TileGrid tiles={IMPORTANT_TILES} section="important" docs={importantDocs} />
        <DocList docs={importantDocs} />
      </section>

      {/* Modal */}
      <Modal
        open={modalOpen}
        title={
          isReplace
            ? 'Replace File'
            : isEdit
              ? 'Edit Document'
              : 'Add Document'
        }
        onCancel={closeModal}
        onOk={() => void saveModal()}
        okText={uploading ? 'Saving…' : 'Save'}
        containerClassName="max-w-lg"
      >
        {editingDoc ? (
          <div className="space-y-3 text-sm">
            {/* Document Name */}
            <label className="block text-stone-700">
              Document Name
              <input
                className="mt-1 w-full rounded-md border border-stone-200 px-2 py-1.5"
                value={editingDoc.name}
                onChange={(e) => patch({ name: e.target.value })}
                disabled={isArchived}
                placeholder="e.g. John Smith Last Will and Testament"
              />
            </label>

            {/* Document Type */}
            <label className="block text-stone-700">
              Document Type
              <select
                className="mt-1 w-full rounded-md border border-stone-200 px-2 py-1.5 bg-white"
                value={editingDoc.type}
                onChange={(e) => patch({ type: e.target.value })}
                disabled={isArchived}
              >
                <optgroup label="Legal Documents">
                  {LEGAL_TILES.map((t) => (
                    <option key={t.type} value={t.type}>
                      {t.label}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Important Documents">
                  {IMPORTANT_TILES.map((t) => (
                    <option key={t.type} value={t.type}>
                      {t.label}
                    </option>
                  ))}
                </optgroup>
              </select>
            </label>

            {/* Description */}
            <label className="block text-stone-700">
              Description
              <textarea
                className="mt-1 w-full min-h-[56px] rounded-md border border-stone-200 px-2 py-1.5"
                value={editingDoc.description}
                onChange={(e) => patch({ description: e.target.value })}
                disabled={isArchived}
                placeholder="Optional description"
              />
            </label>

            {/* Upload File */}
            <div>
              <p className="text-stone-700 mb-1 font-medium">
                {isReplace ? 'Replace File' : 'Upload File'}
              </p>
              {!isReplace && editingDoc.fileName ? (
                <p className="text-xs text-stone-500 mb-1">
                  Current file: <span className="font-medium">{editingDoc.fileName}</span>
                </p>
              ) : null}
              <input
                type="file"
                accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                disabled={isArchived || uploading}
                onChange={(e) => setPendingFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-stone-700 file:mr-3 file:rounded file:border file:border-stone-200 file:bg-stone-50 file:px-3 file:py-1 file:text-xs file:font-medium hover:file:bg-stone-100"
              />
              {pendingFile ? (
                <p className="mt-1 text-xs text-stone-500">
                  Selected: <span className="font-medium">{pendingFile.name}</span>
                </p>
              ) : null}
            </div>

            {/* Notes */}
            <label className="block text-stone-700">
              Notes
              <textarea
                className="mt-1 w-full min-h-[56px] rounded-md border border-stone-200 px-2 py-1.5"
                value={editingDoc.notes}
                onChange={(e) => patch({ notes: e.target.value })}
                disabled={isArchived}
                placeholder="Optional notes"
              />
            </label>

            {/* Settlement fields */}
            <div className="border-t border-stone-200 pt-3 mt-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-stone-500">
                Settlement Fields
              </p>

              {/* Status */}
              <label className="block text-stone-700 mb-3">
                Status
                <select
                  className="mt-1 w-full rounded-md border border-stone-200 px-2 py-1.5 bg-white"
                  value={editingDoc.status}
                  onChange={(e) => patch({ status: e.target.value as DocStatus })}
                  disabled={isArchived}
                >
                  {DOC_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>

              {/* Access Status */}
              <label className="block text-stone-700">
                Access Status
                <select
                  className="mt-1 w-full rounded-md border border-stone-200 px-2 py-1.5 bg-white"
                  value={editingDoc.accessStatus}
                  onChange={(e) => patch({ accessStatus: e.target.value as DocAccessStatus })}
                  disabled={isArchived}
                >
                  {ACCESS_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        ) : null}
      </Modal>
    </SettlementStepPage>
  );
}
